import { useEffect, useState, ReactElement } from 'react';
import IdleTimer from 'react-idle-timer';
import sf from 'seconds-formater';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import Paper from '@material-ui/core/Paper';
import { Grid } from '@mui/material';

import useCounter from './hooks/useCounter';
import Loader from '~/ui/components/common/Loader';
import AlertModal from '~/ui/components/common/AlertModal';

import { INACTIVITY_INTERVAL_MS } from './constants';
import { IPatientInfo } from '~/services/api/patients/types';
import styles from './ActivityCounter.module.scss';
import { TimerDonutChart } from './TimerDonutChart';

interface IProps {
  patient: IPatientInfo;
  initialCount?: number;
  className?: string;
}

const ActivityCounter = ({ patient, initialCount, className }: IProps): ReactElement => {
  const [showPausedPopup, setShowPausedPopup] = useState(false);

  const { count, start, stop } = useCounter(initialCount);

  const handlePauseCount = () => {
    stop();
    setShowPausedPopup(true);
  };

  const handleResumeCount = () => {
    start();
    setShowPausedPopup(false);
  };

  useEffect(() => {
    start();

    return () => {
      stop();
    };
  }, []);

  const handleOnIdle = () => {
    handlePauseCount();
  };

  return (
    <Paper elevation={1} className={classnames(styles.activityTimer, className)}>
      {isEmpty(patient) ? (
        <Loader />
      ) : (
        <Grid container justifyContent="center" className={styles.container}>
          <Grid container item flexWrap="nowrap" className={styles.totalTimeContainer}>
            <Grid container alignItems="center" alignContent="center">
              <Grid container justifyContent="center" className={styles.totalTimeLabel}>
                Total Time
              </Grid>
              <Grid container justifyContent="center" className={styles.totalTimeValue}>
                {sf.convert(patient.activitySecondsCount + count).format()}
              </Grid>
            </Grid>
            <Grid className={styles.timerDonutChartContainer}>
              <TimerDonutChart radius={25} secondsCount={patient.activitySecondsCount + count} />
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent="center" className={styles.name}>
            {`${patient.fullName} - ${patient.subjectId}`}
          </Grid>
          <Grid container item xs={12}>
            <Grid container justifyContent="center" className={styles.currentSession}>
              Current Session
            </Grid>
            <Grid container justifyContent="center" className={styles.timer}>
              {sf.convert(count).format()}
            </Grid>
          </Grid>
          {showPausedPopup && (
            <AlertModal
              title="Timer Paused"
              description="Timer Paused due to Inactivity"
              btnText="Continue"
              onClose={() => handleResumeCount()}
            />
          )}
          <IdleTimer timeout={INACTIVITY_INTERVAL_MS} onIdle={handleOnIdle} debounce={1000} />
        </Grid>
      )}
    </Paper>
  );
};

export default ActivityCounter;
